import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { Search, CheckCircle2, XCircle, Award, Calendar, Hash, User, Loader2 } from "lucide-react";
import { CertificateBadge } from "@/components/learn/CertificateBadge";
import { SEO } from "@/components/SEO";

interface CertificateResult {
  valid: boolean;
  certificate_number: string;
  score: number;
  issued_at: string;
  user_email?: string;
}

export default function VerifyCertificate() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [certificateId, setCertificateId] = useState(searchParams.get('id') || '');
  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<CertificateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Auto-verify if ID is in URL
  useEffect(() => {
    const id = searchParams.get('id');
    if (id && id.trim()) {
      setCertificateId(id);
      handleVerify(id);
    }
  }, [searchParams]);

  const handleVerify = async (id?: string) => {
    const certId = id || certificateId;
    if (!certId.trim()) {
      setError(t('verifyCertificate.emptyError', 'Please enter a certificate number'));
      return;
    }

    setIsVerifying(true);
    setError(null);
    setResult(null);
    setHasSearched(true);

    try {
      const { data, error: fetchError } = await supabase
        .from('latex_certificates')
        .select('certificate_number, score, issued_at, passed')
        .eq('certificate_number', certId.trim())
        .eq('passed', true)
        .single();

      if (fetchError || !data) {
        setResult(null);
        setError(t('verifyCertificate.notFound', 'Certificate not found or invalid'));
      } else {
        setResult({
          valid: true,
          certificate_number: data.certificate_number || '',
          score: data.score,
          issued_at: data.issued_at || '',
        });
      }
    } catch (err) {
      setError(t('verifyCertificate.error', 'An error occurred while verifying'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleVerify();
  };

  const formattedDate = result?.issued_at 
    ? new Date(result.issued_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : '';

  return (
    <PageLayout>
      <SEO 
        title={t('verifyCertificate.seoTitle', 'Verify Certificate - CalcLab')}
        description={t('verifyCertificate.seoDescription', 'Verify the authenticity of CalcLab LaTeX Professional Certification certificates')}
      />
      
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <div className="text-center mb-8">
          <Award className="h-16 w-16 text-primary mx-auto mb-4" />
          <h1 className="text-3xl font-bold mb-2">
            {t('verifyCertificate.title', 'Verify Certificate')}
          </h1>
          <p className="text-muted-foreground">
            {t('verifyCertificate.subtitle', 'Enter a certificate number to verify its authenticity')}
          </p>
        </div>

        {/* Search Form */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="flex gap-3">
              <div className="flex-1 relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={certificateId}
                  onChange={(e) => setCertificateId(e.target.value)}
                  placeholder={t('verifyCertificate.placeholder', 'CALC-LaTeX-XXXXXX')}
                  className="pl-10 font-mono"
                />
              </div>
              <Button type="submit" disabled={isVerifying}>
                {isVerifying ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    {t('verifyCertificate.verify', 'Verify')}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Results */}
        {hasSearched && !isVerifying && (
          <>
            {result?.valid ? (
              <Card className="border-2 border-green-500 bg-green-500/5">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-8 w-8 text-green-600" />
                    <div>
                      <CardTitle className="text-green-700 dark:text-green-400">
                        {t('verifyCertificate.validTitle', 'Valid Certificate')}
                      </CardTitle>
                      <CardDescription>
                        {t('verifyCertificate.validDescription', 'This certificate is authentic and verified')}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Certificate Details */}
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-background">
                      <Hash className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {t('verifyCertificate.certificateNumber', 'Certificate Number')}
                        </p>
                        <p className="font-mono font-medium">{result.certificate_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-background">
                      <Award className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {t('verifyCertificate.score', 'Score')}
                        </p>
                        <p className="font-medium">{result.score}/100</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-background sm:col-span-2">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">
                          {t('verifyCertificate.issuedOn', 'Issued On')}
                        </p>
                        <p className="font-medium">{formattedDate}</p>
                      </div>
                    </div>
                  </div>

                  {/* Badge Preview */}
                  <div className="pt-4 border-t">
                    <h3 className="text-center font-medium mb-4">
                      {t('verifyCertificate.badgePreview', 'Certificate Badge')}
                    </h3>
                    <CertificateBadge
                      userName="Verified User"
                      certificateNumber={result.certificate_number}
                      issuedAt={result.issued_at}
                      canDownload={false}
                    />
                  </div>
                </CardContent>
              </Card>
            ) : error ? (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertTitle>{t('verifyCertificate.invalidTitle', 'Invalid Certificate')}</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
          </>
        )}

        {/* Info Section */}
        <Card className="mt-8 bg-muted/30">
          <CardContent className="pt-6">
            <h3 className="font-medium mb-2">
              {t('verifyCertificate.aboutTitle', 'About CalcLab LaTeX Certification')}
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              {t('verifyCertificate.aboutDescription', 'The CalcLab LaTeX Professional Certification validates proficiency in creating professional LaTeX documents including reports, academic papers, and technical documentation. Certificate holders have demonstrated skills in document structure, mathematical notation, tables, lists, and professional formatting.')}
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">LaTeX</Badge>
              <Badge variant="secondary">Document Formatting</Badge>
              <Badge variant="secondary">Mathematical Notation</Badge>
              <Badge variant="secondary">Professional Reports</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
